import type { AliasToken, MappingAlgorithm } from 'antd/es/theme/interface';

import {
    defaultDarkComponentTokens,
    defaultDarkTokens,
    defaultLightComponentTokens,
    defaultLightTokens,
    platformDarkAlgorithm,
    platformLightAlgorithm,
} from '@/theme';
import type { ComponentsConfig } from '@/theme';

export type ThemeName = string;

export const WORKSPACE_WHITELABEL_THEME_NAME = 'workspace-whitelabel';

export interface WorkspaceThemeConfiguration {
    darkThemeName: ThemeName;
    lightThemeName: ThemeName;
    supportsColorSchemes: boolean;
}

export interface ThemeSelectionOption {
    disabled: boolean;
    label: string;
    name: ThemeName;
    type: 'dark' | 'light' | 'system' | 'workspace';
}

export interface AppTheme {
    name: ThemeName;
    // the tokens object will be spread into ConfigProvider.theme.token
    tokens?: Partial<AliasToken>;
    // use antd algorithms: defaultAlgorithm, darkAlgorithm
    algorithm?: MappingAlgorithm | MappingAlgorithm[];
    // optional per-component overrides for antd
    components?: ComponentsConfig;
}

export const normalizeThemeSelectionName = (name: ThemeName): ThemeName =>
    name.startsWith(`${WORKSPACE_WHITELABEL_THEME_NAME}-`) ? WORKSPACE_WHITELABEL_THEME_NAME : name;

export const getThemeSelectionOptions = (workspaceTheme?: WorkspaceThemeConfiguration): ThemeSelectionOption[] => {
    const standardThemesDisabled = Boolean(workspaceTheme);
    const standardThemes: ThemeSelectionOption[] = [
        { disabled: standardThemesDisabled, label: 'Dark', name: 'dark', type: 'dark' },
        { disabled: standardThemesDisabled, label: 'Light', name: 'light', type: 'light' },
        { disabled: standardThemesDisabled, label: 'System', name: 'system', type: 'system' },
    ];

    if (!workspaceTheme) {
        return standardThemes;
    }

    return [
        {
            disabled: false,
            label: 'Workspace Theme',
            name: WORKSPACE_WHITELABEL_THEME_NAME,
            type: 'workspace',
        },
        ...standardThemes,
    ];
};

export const resolveThemeSelection = (
    name: ThemeName,
    prefersDark: boolean,
    workspaceTheme?: WorkspaceThemeConfiguration,
): ThemeName => {
    const normalizedName = normalizeThemeSelectionName(name);

    if (normalizedName === 'system') {
        return prefersDark ? 'dark' : 'light';
    }

    if (normalizedName === WORKSPACE_WHITELABEL_THEME_NAME && workspaceTheme) {
        return workspaceTheme.supportsColorSchemes && prefersDark
            ? workspaceTheme.darkThemeName
            : workspaceTheme.lightThemeName;
    }

    return name;
};

export const useThemeConfig = () => {
    const systemTheme: AppTheme = {
        name: 'system',
    };

    const builtInThemes: AppTheme[] = [
        {
            name: 'light',
            tokens: defaultLightTokens,
            algorithm: platformLightAlgorithm,
            components: defaultLightComponentTokens,
        },
        {
            name: 'dark',
            tokens: defaultDarkTokens,
            algorithm: platformDarkAlgorithm,
            components: defaultDarkComponentTokens,
        },
        systemTheme,
    ];

    return { builtInThemes, defaultLightTokens, defaultDarkTokens };
};
